import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, BedDouble, CalendarClock, Loader2, MapPin, Phone } from "lucide-react";
import { VisitSheet } from "@/components/VisitSheet";
import { residentApi, errMsg } from "@/lib/api";
import { useApi } from "@/lib/useApi";
import { useResident } from "@/lib/residentAuth";
import { inr, todayISO } from "@/lib/format";
import { Card, ErrorState, ListSkeleton } from "@/components/common";
import { BottomSheet } from "@/components/BottomSheet";
import { Field, BigInput, Chips } from "@/components/form";
import { AMENITY, GENDER } from "@/pages/resident/FindPG";
import { PhotoGallery } from "@/components/Photos";
import { ListingRooms } from "@/components/ListingRooms";

function JoinSheet({ l, room, open, onClose }: { l: any; room: any; open: boolean; onClose: () => void }) {
  const { resident } = useResident();
  const nav = useNavigate();
  const [f, setF] = useState<any>({ name: resident?.name || "", phone: resident?.phone || "", move_in_date: todayISO(), sharing: l.sharing_options[0] ? String(l.sharing_options[0]) : "", message: "" });
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await residentApi.post("/resident/join-requests", { ...f, property_id: l.id, sharing: room ? room.sharing : f.sharing ? Number(f.sharing) : null, room_id: room?.id || null });
      toast.success("Request sent to the PG owner");
      onClose();
      nav("/resident");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <BottomSheet open={open} onOpenChange={(o) => !o && onClose()} title={room ? `Request Room ${room.name}` : "Request to join"} description={l.name} testId="join-sheet">
      <div className="space-y-4">
        {room && (
          <div data-testid="join-room-summary" className="rounded-xl bg-[#e8f1fb] text-[#131e2e] p-3 text-sm">
            <b>Room {room.name}</b>{room.floor ? ` · Floor ${room.floor}` : ""} · {room.sharing === 1 ? "Single" : `${room.sharing}-sharing`} · {inr(room.rent_per_bed)}/bed
          </div>
        )}
        <Field label="Your full name"><BigInput data-testid="join-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label="Mobile number"><BigInput data-testid="join-phone-input" inputMode="numeric" maxLength={10} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value.replace(/\D/g, "") })} /></Field>
        <Field label="Preferred move-in date"><BigInput data-testid="join-date-input" type="date" min={todayISO()} value={f.move_in_date} onChange={(e) => setF({ ...f, move_in_date: e.target.value })} /></Field>
        {!room && l.sharing_options.length > 0 && (
          <Field label="Sharing preference">
            <Chips testId="join-sharing" value={f.sharing} onChange={(v) => setF({ ...f, sharing: v })} options={l.sharing_options.map((s) => ({ value: String(s), label: s === 1 ? "Single" : `${s} sharing` }))} />
          </Field>
        )}
        <Field label="Message (optional)"><Textarea data-testid="join-message-input" rows={3} maxLength={500} className="rounded-xl text-base bg-white" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="e.g. I work nearby, vegetarian" /></Field>
        <Button data-testid="join-submit-btn" disabled={busy || !f.name.trim() || !/^[6-9]\d{9}$/.test(f.phone)} onClick={send} className="w-full h-14 rounded-xl text-base bg-[#131e2e] hover:bg-[#0b1320]">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send request"}
        </Button>
      </div>
    </BottomSheet>
  );
}

export default function ListingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { resident } = useResident();
  const { data: l, loading, error, reload } = useApi<any>(`/listings/${id}`);
  const [open, setOpen] = useState(false);
  const [visitOpen, setVisitOpen] = useState(false);
  const goHome = useNavigate();
  const [room, setRoom] = useState<any>(null);
  const openJoin = (r: any) => { setRoom(r); setOpen(true); };
  const [params, setParams] = useSearchParams();
  const wanted = params.get("room");
  useEffect(() => {
    if (!wanted || !l) return;
    const r = l.rooms?.find((x) => x.id === wanted);
    if (r && r.vacant_beds > 0 && !resident?.stay) openJoin(r);
    setParams({}, { replace: true });
  }, [wanted, l]); // eslint-disable-line react-hooks/exhaustive-deps
  if (loading && !l) return <ListSkeleton rows={4} />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  return (
    <div className="space-y-4">
      <button data-testid="listing-back-btn" onClick={() => nav(-1)} className="h-10 flex items-center gap-1 text-sm font-semibold text-slate-600"><ArrowLeft className="h-4 w-4" />Back</button>
      <PhotoGallery photos={(l.photos || []).some((p) => !p.room_id) ? l.photos.filter((p) => !p.room_id) : l.photos} />
      <div className="pg-rise">
        <span className="text-[11px] font-bold rounded-full bg-[#e8f1fb] text-[#131e2e] px-2.5 py-1">{GENDER[l.gender]}</span>
        <h1 data-testid="listing-name" className="mt-2 text-3xl font-extrabold tracking-tight">{l.name}</h1>
        <p className="text-sm text-slate-500 flex items-center gap-1 mt-1"><MapPin className="h-3.5 w-3.5" />{l.address}{l.city ? `, ${l.city}` : ""}</p>
      </div>
      <Card className="p-4 grid grid-cols-2 gap-3">
        <div><p className="text-xs text-slate-500">Rent per bed</p><p className="text-lg font-extrabold font-mono-num text-[#131e2e]">{inr(l.rent_min)}{l.rent_max > l.rent_min ? `–${inr(l.rent_max)}` : ""}</p></div>
        <div><p className="text-xs text-slate-500">Available</p><p className="text-lg font-extrabold flex items-center gap-1"><BedDouble className="h-4 w-4" />{l.vacant_beds} beds</p></div>
        {l.sharing_options.length > 0 && <p className="col-span-2 text-xs text-slate-500">Sharing with free beds: {l.sharing_options.map((s) => (s === 1 ? "Single" : `${s}-sharing`)).join(", ")}</p>}
      </Card>
      {l.description && <Card className="p-4"><p className="font-bold mb-1">About</p><p className="text-sm text-slate-600 whitespace-pre-line">{l.description}</p></Card>}
      <ListingRooms rooms={l.rooms} onRequest={resident?.stay ? undefined : openJoin} />
      {l.amenities.length > 0 && (
        <Card className="p-4"><p className="font-bold mb-2">Amenities</p>
          <div className="flex flex-wrap gap-2">{l.amenities.map((a) => <span key={a} className="text-sm rounded-full bg-slate-100 px-3 py-1.5">{AMENITY[a]}</span>)}</div>
        </Card>
      )}
      {l.contact_phone && <a data-testid="listing-call-btn" href={`tel:+91${l.contact_phone}`} className="h-12 rounded-xl border border-slate-200 bg-white font-semibold flex items-center justify-center gap-2"><Phone className="h-4 w-4" />Call owner</a>}
      {resident?.stay ? (
        <p className="text-sm text-slate-500 text-center">You're already staying in a PG.</p>
      ) : (
        <Button data-testid="listing-join-btn" disabled={!l.vacant_beds} onClick={() => openJoin(null)} className="w-full h-14 rounded-xl text-base bg-[#131e2e] hover:bg-[#0b1320]">
          {l.vacant_beds ? "Request to join" : "No beds available"}
        </Button>
      )}
      {!resident?.stay && (
        <Button data-testid="listing-visit-btn" variant="outline" onClick={() => setVisitOpen(true)} className="w-full h-12 rounded-xl text-base border-2 border-[#131e2e] text-[#131e2e] -mt-1">
          <CalendarClock className="h-5 w-5 mr-2" />Book a visit
        </Button>
      )}
      {open && <JoinSheet l={l} room={room} open={open} onClose={() => setOpen(false)} />}
      {visitOpen && <VisitSheet l={l} open={visitOpen} onClose={() => setVisitOpen(false)} onBooked={() => goHome("/resident")} />}
    </div>
  );
}
